import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Box, CircularProgress, Fade, Grid } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Fragment, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

import { CmsArticles, fetchCmsArticle } from '../../../../shared/helpers/cms-articles';
import AppSummary from '../../../components/AppSummary/app-summary';
import { CaptureSignature } from '../../../components/CaptureSignature/capture-signature';
import { completeApplicationAsync } from '../../../redux/scholarship.actions';
import { EnumAppSignatureFormat, EnumAppSignatureType, MAppSignature } from '../../../redux/scholarship.models';
import { selectSignatureParent } from '../../../redux/scholarship.selectors';
import { ApplicationFormContainer } from '../../ApplicationsForm/applications-form.styles';

const useStyles = makeStyles((theme) =>
  createStyles({
    cardTop: {
      marginTop: theme.spacing(3)
    },
    formControlLabel: {
      fontSize: '0.9rem'
    },
    radio: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1)
    },
    button: {
      height: '45px',
      marginLeft: '15px',
      marginRight: '15px'
    },
    buttons: {
      width: '100%',
      textAlign: 'center'
    },
    buttonSmall: {
      marginRight: theme.spacing(1),
      height: '25px',
      font: '15px;',
      backgroundColor: '#f87c7c'
    },
    rootLeft: {
      width: '100%',
      textAlign: 'right',
      marginTop: '40px'
    }
  })
);

const ApplicationsFormSignatures = ({ id, dispatchCompleteApplication, scanningTranscript, scanningEssay, focusErrors }) => {
  const [completed, setCompleted] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const parentSignatures = useSelector<unknown, MAppSignature[]>(selectSignatureParent as (state: unknown) => MAppSignature[]);
  const parentSignature: MAppSignature | undefined = parentSignatures[0];
  const [data, setData] = useState(localStorage.getItem('legal') || '');
  const [isFetching, setIsFetching] = useState(false);
  const { trackPageView } = useMatomo();

  // Track page view
  useEffect(() => {
    trackPageView({});
  }, []);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setIsFetching(true);
        const response = await fetchCmsArticle(CmsArticles.APPLICATION_SIGNATURES);
        setData(response);
        localStorage.setItem('legal', response || '');
        setIsFetching(false);
      } catch (error) {
        console.error(error);
        if (setData) {
          setData('Error loading');
        }
        if (setIsFetching) {
          setIsFetching(false);
        }
      }
    };

    void fetchArticle();
  }, []);

  const changeCallback = async (value: {
    firstName: string;
    lastName: string;
    checked: boolean;
    signed: boolean;
    signedAgreementWording: string;
    signatureType?: string;
    signatureFormat?: string;
  }): Promise<void> => {
    if (parentSignature) {
      parentSignature.signed = !!value.signed;
    }
  };

  const handleSubmit = () => {
    if (!parentSignature) {
      focusErrors();

      return;
    }

    setLoading(true);
    parentSignature.signedAgreementWording = data;
    dispatchCompleteApplication(id, parentSignature).then((result) => {
      if (result) {
        setLoading(false);
        setCompleted(true);
      } else {
        setLoading(false);
        focusErrors();
      }
    });
  };

  const renderNotCompleted = () => (
    <div>
      <ApplicationFormContainer>
        <Fragment>
          <Typography variant="body2" gutterBottom component="div">
            <div className="lead" dangerouslySetInnerHTML={{ __html: data }} />
            <Fade in={isFetching} timeout={5}>
              <Box m="auto">
                <CircularProgress />
              </Box>
            </Fade>
          </Typography>
        </Fragment>
        <CaptureSignature
          signedAgreementWording={data}
          parentLabel="Parent/Guardian Signature"
          childLabel="Child Name"
          disableNames={true}
          initialFirstName={parentSignature?.firstName ?? ''}
          initialLastName={parentSignature?.lastName ?? ''}
          initialChildFirstName=""
          initialChildLastName=""
          initialChecked={false}
          initialSignatureType={EnumAppSignatureType.PARENT}
          initialSignatureFormat={EnumAppSignatureFormat.DIGITAL}
          changeCallback={changeCallback}
        />
      </ApplicationFormContainer>
      <Grid container justifyContent="center" alignItems="center">
        <Grid item xs={12}>
          <div className={classes.buttons}>
            <Button variant="contained" color="primary" className={classes.button} onClick={() => handleSubmit()}>
              Submit
              {loading && (
                <Box ml={2} mt={1}>
                  <CircularProgress size="0.8rem" color={'inherit'} />
                </Box>
              )}
            </Button>
          </div>
        </Grid>
      </Grid>
    </div>
  );

  const renderCompleted = () => (
    <Fragment>
      <Typography variant="h6" gutterBottom>
        Your application was successfully submitted.
      </Typography>
      <Navigate to="/applications" replace />
    </Fragment>
  );

  return (
    <Box pt={3} pb={3} pl={1}>
      <Fragment>
        {completed && renderCompleted()}
        <Typography variant="h6" gutterBottom>
          Application Summary
        </Typography>
        <AppSummary scanningEssay={scanningEssay} scanningTranscript={scanningTranscript} />
      </Fragment>
      {!completed && renderNotCompleted()}
    </Box>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchCompleteApplication: (id: string, value: unknown): Promise<unknown> => {
    return dispatch(completeApplicationAsync(id, value)) as Promise<unknown>;
  }
});
export default connect(null, mapDispatchToProps)(ApplicationsFormSignatures);
